import {
  CAGRDashboardDataTransformer,
  ChartFieldDashboardDataTransformer,
  SortByTimeColumnDataTransformer,
  SumSubMarketNameDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from 'src/app/dashboards/shared/data_transformers/dashboard_data_transformer';
import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { axesView, composeHeading, createRouteMethodologyFilter, defineDashboard, isKEur, resolveProduct } from '../../../_shared/dashboard-axes';
import { createPeriodLabelTransformers, createPeriodYearTransformers, createTimeFilters, resolveTimeFilter, timeFilterDetail } from '../../../_shared/time-filters';
import {
  cagrIndexColumnsOf,
  createIdNameTransformers,
  granularityHeadingPhrase,
  groupDetailOf,
  mapHeadingPhrase,
  mapLabelKeyOf,
} from '../../../../dashboard-config/granularities/granularity-profile';
import { kpiColumn } from '../../../../dashboard-config/kpis/kpi-columns';
import {
  KpiColumnDataTransformer,
  KpiExportDataTransformer,
  KpiLabelDataTransformer,
  KpiValueDisplayDataTransformer,
} from '../../../../dashboard-config/kpis/kpi-data-transformers';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import { DashboardTimeSeriesBarDefinition } from './dashboard-time-series-bar.definition';
import {
  TIME_SERIES_BAR_KEYS,
  TIME_SERIES_BAR_MEASURES,
  TimeSeriesBarMetricProfile,
  timeSeriesChartHeadingLead,
  timeSeriesMapHeadingLead,
} from './dashboard-time-series-bar-metric.profile';
import {
  DashboardTimeSeriesBarShell,
  dashboardTimeSeriesBarShellProfiles,
} from './dashboard-time-series-bar-shell.profile';

export type CreateDashboardTimeSeriesBarDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardTimeSeriesBarShell,
    TimeSeriesBarMetricProfile
  >;

export function createDashboardTimeSeriesBarDefinition(
  input: CreateDashboardTimeSeriesBarDefinitionInput,
): DashboardTimeSeriesBarDefinition {
  const { axes, shell, metric } = resolveProduct(input, dashboardTimeSeriesBarShellProfiles);
  const granularityId = axes.granularity.id;
  const context = axes.kpiContext;

  /* ---- Time ---- */
  const time = resolveTimeFilter(axes, 'range');
  const { series } = timeFilterDetail(time);

  /* ---- Headings ---- */
  const chartHeadingLead = timeSeriesChartHeadingLead(metric);
  const mapHeadingLead = timeSeriesMapHeadingLead(metric);

  return defineDashboard<DashboardTimeSeriesBarDefinition>(axes, shell, {
    heading: (filterValues) =>
      composeHeading(
        [
          chartHeadingLead(filterValues),
          shell.chartHeadingGranularity?.(filterValues) ?? granularityHeadingPhrase(granularityId, filterValues),
        ],
        filterValues.get(series.filterName),
      ),
    mapHeading: (filterValues) =>
      composeHeading([
        mapHeadingLead(filterValues),
        shell.mapHeadingGranularity?.(filterValues) ?? mapHeadingPhrase(granularityId, filterValues),
      ]),

    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...shell.buildGranularityFilters(deps, {
        useRemoteSubMarket: metric.useRemoteSubMarket,
        statesDefaultValues: axes.selections?.states && [...axes.selections.states],
        districtsDefaultValues: axes.selections?.districts && [...axes.selections.districts],
      }),
      ...createKpiFilters(axes.metric, granularityId),
      createRouteMethodologyFilter(deps),
    ],

    preFilterTransformers: (deps) => [
      new UndefinedStateToSumGermanyDashboardDataTransformer(),
      ...createPeriodLabelTransformers(deps, axes),
    ],

    // Order matches the legacy post-filter service: time sort → period year → CAGR → id maps.
    postFilterTransformers: (deps) => [
      new SortByTimeColumnDataTransformer(metric.sortTimeColumn ?? 'year'),
      ...createPeriodYearTransformers(axes),
      // CAGR of every KPI, so switching the KPI needs no recompute.
      ...axes.metric.kpis().map(
        (kpi) =>
          new CAGRDashboardDataTransformer({
            valueColumnKey: kpiColumn(kpi, 'value', context),
            resultColumnKey: kpiColumn(kpi, 'cagr', context),
            indexColumns: cagrIndexColumnsOf(granularityId),
          }),
      ),
      new SumSubMarketNameDataTransformer(deps.localDatasetsService),
      ...createIdNameTransformers(deps, granularityId),
      new KpiLabelDataTransformer(),
    ],

    chartTransformers: () => [
      ...TIME_SERIES_BAR_MEASURES.map(
        ({ measure, resultColumn }) => new KpiColumnDataTransformer({ measure, resultColumn, context }),
      ),
      new KpiValueDisplayDataTransformer({
        valueColumnKey: TIME_SERIES_BAR_KEYS.valueKey,
        resultColumnKey: TIME_SERIES_BAR_KEYS.valueKey + '_display',
      }),
    ],

    exportTransformers: () => [
      new KpiExportDataTransformer({
        kpis: axes.metric.kpis(),
        percentageMeasures: metric.exportPercentageMeasures ?? ['changePrevYearPct', 'cagr'],
        context,
      }),
    ],

    mapTransformers: () => [
      new ChartFieldDashboardDataTransformer('value_label', (row) =>
        row[shell.mapValueLabelKey ?? mapLabelKeyOf(granularityId)],
      ),
    ],

    view: {
      seriesFilterDetail: series,
      chartGroupDetail: groupDetailOf(granularityId, 'code'),
      mainBarLabelHeight: metric.resolveMainBarLabelHeight?.(axes.granularity) ?? 85,
      kEur: isKEur(axes),
      ...axesView(axes),
      showMapSidebar: metric.showMapSidebar ?? true,
    },
  });
}
